import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  ArrowsPointingOutIcon,
  MinusIcon,
  PlusIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import type { PassportCard as Card } from '../../../services/api/passportCards';
import CollectorCard from '../CollectorCard';
import { useCollectionMotion } from './useCollectionMotion';
import type { CanvasPosition } from './collectionState';

type Camera = { x: number; y: number; zoom: number };
type DragState = {
  key: string;
  pointerId: number;
  startX: number;
  startY: number;
  position: CanvasPosition;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const clampZoom = (value: number) => clamp(value, 0.12, 1.5);
function defaultPosition(index: number, total: number): CanvasPosition {
  const columns = Math.min(6, Math.max(3, Math.ceil(Math.sqrt(Math.max(1, total) * 1.25))));
  const rows = Math.max(1, Math.ceil(Math.max(1, total) / columns));
  const column = index % columns;
  const row = Math.floor(index / columns);
  return {
    key: '',
    x: ((column + 0.5) / columns) * 100,
    y: ((row + 0.5) / rows) * 100,
    rotation: ((index * 17) % 13) - 6,
    scale: 1,
    z: index,
  };
}

export default function PassportExploreCanvas({
  cards,
  positions,
  editable,
  onOpen,
  onPositionChange,
  onSave,
  onCancel,
  saving,
  saveError,
}: {
  cards: Card[];
  positions: CanvasPosition[];
  editable: boolean;
  onOpen: (card: Card) => void;
  onPositionChange: (position: CanvasPosition) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  saveError: string;
}) {
  const instructionsId = useId();
  const viewport = useRef<HTMLDivElement>(null);
  const motionRoot = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [camera, setCamera] = useState<Camera>({ x: 24, y: 24, zoom: 1 });
  const cameraRef = useRef(camera);
  const [dragging, setDragging] = useState(false);
  const [dragPreview, setDragPreview] = useState<CanvasPosition | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const movedRef = useRef(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const travel = useRef<number | null>(null);
  const destination = useRef<Camera | null>(null);
  const fitted = useRef(false);
  const world = useMemo(() => {
    const columns = Math.min(6, Math.max(3, Math.ceil(Math.sqrt(Math.max(1, cards.length) * 1.25))));
    const rows = Math.max(1, Math.ceil(Math.max(1, cards.length) / columns));
    return { width: columns * 230, height: rows * 300 };
  }, [cards.length]);
  const saved = useMemo(() => new Map(positions.map((position) => [position.key, position])), [positions]);
  const visiblePositions = useMemo(
    () =>
      new Map(
        cards.map((card, index) => {
          const key = card.key!;
          return [
            key,
            saved.get(key) || { ...defaultPosition(index, cards.length), key },
          ] as const;
        })
      ),
    [cards, saved]
  );
  const maxZ = useMemo(
    () =>
      Math.max(
        0,
        ...positions.map((position) => position.z),
        ...Array.from(visiblePositions.values()).map((position) => position.z)
      ),
    [positions, visiblePositions]
  );
  const signature = cards.map((card) => card.key).join('|');

  useCollectionMotion(motionRoot, `explore:${signature}`, { maxAnimated: Infinity });

  const stopTravel = useCallback(() => {
    if (travel.current !== null) cancelAnimationFrame(travel.current);
    travel.current = null;
  }, []);
  const moveCamera = useCallback(
    (target: Camera, animated = true) => {
      stopTravel();
      destination.current = target;
      if (
        !animated ||
        process.env.NODE_ENV === 'test' ||
        window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
      ) {
        cameraRef.current = target;
        setCamera(target);
        return;
      }
      const from = cameraRef.current;
      const started = performance.now();
      const frame = (now: number) => {
        const progress = Math.min(1, (now - started) / 460);
        const ease = 1 - Math.pow(1 - progress, 4);
        const next = {
          x: from.x + (target.x - from.x) * ease,
          y: from.y + (target.y - from.y) * ease,
          zoom: from.zoom + (target.zoom - from.zoom) * ease,
        };
        cameraRef.current = next;
        setCamera(next);
        travel.current = progress < 1 ? requestAnimationFrame(frame) : null;
      };
      travel.current = requestAnimationFrame(frame);
    },
    [stopTravel]
  );
  const fit = useCallback(
    (animated = true) => {
      const zoom = clampZoom(
        Math.min((size.width - 36) / world.width, (size.height - 36) / world.height, 1)
      );
      moveCamera(
        {
          zoom,
          x: (size.width - world.width * zoom) / 2,
          y: (size.height - world.height * zoom) / 2,
        },
        animated
      );
    },
    [moveCamera, size, world]
  );
  const zoomAt = useCallback(
    (factor: number, x: number, y: number, animated = false) => {
      stopTravel();
      const current = cameraRef.current;
      const zoom = clampZoom(current.zoom * factor);
      const target = {
        zoom,
        x: x - ((x - current.x) * zoom) / current.zoom,
        y: y - ((y - current.y) * zoom) / current.zoom,
      };
      moveCamera(target, animated);
    },
    [moveCamera, stopTravel]
  );

  useEffect(() => {
    const el = viewport.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) =>
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height })
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!size.width || fitted.current) return;
    fitted.current = true;
    fit(false);
  }, [fit, size.width]);
  useEffect(
    () => () => {
      stopTravel();
      pointers.current.clear();
    },
    [stopTravel]
  );
  useEffect(() => {
    cameraRef.current = camera;
  }, [camera]);
  useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const wheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      zoomAt(Math.exp(-event.deltaY * 0.006), event.clientX - rect.left, event.clientY - rect.top);
    };
    const move = (event: PointerEvent) => {
      const activeDrag = dragRef.current;
      if (activeDrag && activeDrag.pointerId === event.pointerId) {
        event.preventDefault();
        const rect = el.getBoundingClientRect();
        const dx = (event.clientX - activeDrag.startX) / cameraRef.current.zoom;
        const dy = (event.clientY - activeDrag.startY) / cameraRef.current.zoom;
        if (Math.hypot(dx, dy) > 4) movedRef.current = true;
        const next = {
          ...activeDrag.position,
          x: clamp(activeDrag.position.x + (dx / world.width) * 100, 5, 95),
          y: clamp(activeDrag.position.y + (dy / world.height) * 100, 5, 95),
        };
        if (rect.width > 0) setDragPreview(next);
        return;
      }
      const previous = pointers.current.get(event.pointerId);
      if (!previous) return;
      event.preventDefault();
      const before = Array.from(pointers.current.values());
      pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const after = Array.from(pointers.current.values());
      if (before.length >= 2 && after.length >= 2) {
        const oldDistance = Math.hypot(before[0].x - before[1].x, before[0].y - before[1].y);
        const newDistance = Math.hypot(after[0].x - after[1].x, after[0].y - after[1].y);
        const rect = el.getBoundingClientRect();
        const oldX = (before[0].x + before[1].x) / 2 - rect.left;
        const oldY = (before[0].y + before[1].y) / 2 - rect.top;
        const newX = (after[0].x + after[1].x) / 2 - rect.left;
        const newY = (after[0].y + after[1].y) / 2 - rect.top;
        setCamera((current) => {
          const zoom = clampZoom((current.zoom * newDistance) / Math.max(1, oldDistance));
          return {
            zoom,
            x: newX - ((oldX - current.x) * zoom) / current.zoom,
            y: newY - ((oldY - current.y) * zoom) / current.zoom,
          };
        });
      } else {
        setCamera((current) => ({
          ...current,
          x: current.x + event.clientX - previous.x,
          y: current.y + event.clientY - previous.y,
        }));
      }
    };
    const end = (event: PointerEvent) => {
      const activeDrag = dragRef.current;
      if (activeDrag && activeDrag.pointerId === event.pointerId) {
        const next = dragPreview;
        if (next && movedRef.current) onPositionChange({ ...next, z: maxZ + 1 });
        dragRef.current = null;
        setDragPreview(null);
      }
      pointers.current.delete(event.pointerId);
      if (!pointers.current.size && !dragRef.current) setDragging(false);
    };
    const cancel = () => {
      pointers.current.clear();
      dragRef.current = null;
      setDragPreview(null);
      setDragging(false);
    };
    el.addEventListener('wheel', wheel, { passive: false });
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    window.addEventListener('blur', cancel);
    return () => {
      el.removeEventListener('wheel', wheel);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      window.removeEventListener('blur', cancel);
    };
  }, [dragPreview, maxZ, onPositionChange, zoomAt, world]);

  const beginDrag = (event: React.PointerEvent, card: Card) => {
    if (!editable || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    stopTravel();
    movedRef.current = false;
    const position = visiblePositions.get(card.key!)!;
    dragRef.current = {
      key: card.key!,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      position,
    };
    setDragPreview(position);
    setDragging(true);
  };
  const beginCanvasPan = (event: React.PointerEvent) => {
    if (event.button !== 0 || dragRef.current) return;
    stopTravel();
    movedRef.current = false;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    setDragging(true);
  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.target !== event.currentTarget) return;
    const direction: Record<string, [number, number]> = {
      ArrowLeft: [80, 0],
      ArrowRight: [-80, 0],
      ArrowUp: [0, 80],
      ArrowDown: [0, -80],
    };
    if (direction[event.key]) {
      event.preventDefault();
      const [x, y] = direction[event.key];
      setCamera((current) => ({ ...current, x: current.x + x, y: current.y + y }));
    }
    if (['+', '=', '-'].includes(event.key)) {
      event.preventDefault();
      zoomAt(event.key === '-' ? 0.8 : 1.25, size.width / 2, size.height / 2, true);
    }
    if (event.key === 'Home') {
      event.preventDefault();
      fit();
    }
  };
  const cardPosition = (card: Card, index: number) => {
    const position = dragPreview?.key === card.key ? dragPreview : visiblePositions.get(card.key!);
    return position || { ...defaultPosition(index, cards.length), key: card.key! };
  };

  return (
    <section className="bc-explore" aria-label="Explore your passport collection">
      <div className="bc-explore-toolbar">
        <div>
          <span className="bc-caption">Collection canvas</span>
          <h3>Every find, in view.</h3>
          <p>
            {editable
              ? 'Drag a card handle to place it. Pan the canvas to move through your collection.'
              : 'Zoom out to see the whole collection, then follow the cards that pull you in.'}
          </p>
        </div>
        <div className="bc-explore-toolbar-actions">
          <span className="bc-explore-count" role="status">
            {cards.length} {cards.length === 1 ? 'passport' : 'passports'} in view
          </span>
          <button type="button" onClick={() => fit(true)} aria-label="Fit entire collection">
            <Squares2X2Icon aria-hidden /> Fit all
          </button>
          {editable && (
            <>
              <button type="button" onClick={onCancel} disabled={saving}>
                Cancel
              </button>
              <button
                type="button"
                className="bc-explore-save"
                onClick={onSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save layout'}
              </button>
            </>
          )}
        </div>
      </div>
      {saveError && <div className="bc-explore-error" role="alert">{saveError}</div>}
      <p id={instructionsId} className="bc-sr">
        Drag the canvas to pan. Pinch or hold Control or Command and scroll to zoom. Use arrow keys
        to pan, plus and minus to zoom, and Home to fit the entire collection.
      </p>
      <div
        className="bc-explore-viewport"
        ref={viewport}
        role="region"
        aria-label="Interactive passport collection canvas"
        tabIndex={0}
        aria-describedby={instructionsId}
        data-dragging={dragging}
        onPointerDown={beginCanvasPan}
        onClickCapture={(event) => {
          if (movedRef.current) {
            event.preventDefault();
            event.stopPropagation();
            movedRef.current = false;
          }
        }}
        onKeyDown={handleKeyDown}
      >
        <div
          className="bc-explore-world"
          style={{
            width: world.width,
            height: world.height,
            transform: `translate(${camera.x}px, ${camera.y}px) scale(${camera.zoom})`,
          }}
        >
          <div ref={motionRoot} className="bc-explore-cards">
            {cards.map((card, index) => {
              const position = cardPosition(card, index);
              return (
                <div
                  className={`bc-canvas-card ${editable ? 'is-editable' : ''}`}
                  data-canvas-key={card.key}
                  key={card.key}
                  style={
                    {
                      left: `${position.x}%`,
                      top: `${position.y}%`,
                      zIndex: position.z,
                      transform: `translate(-50%, -50%) rotate(${position.rotation}deg) scale(${position.scale})`,
                    } as React.CSSProperties
                  }
                >
                  <div
                    className="bc-canvas-card-motion"
                    data-binder-key={card.key}
                    onPointerDown={(event) => {
                      if (!editable || (event.target as HTMLElement).closest('button,a')) return;
                      beginDrag(event, card);
                    }}
                  >
                    <CollectorCard card={card} onOpen={() => onOpen(card)} openLabel="Inspect passport" />
                  </div>
                  {editable && (
                    <button
                      type="button"
                      className="bc-canvas-grip"
                      aria-label={`Drag ${card.title} to reposition`}
                      onPointerDown={(event) => beginDrag(event, card)}
                      onPointerUp={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onPointerCancel={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                    >
                      <ArrowsPointingOutIcon aria-hidden />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {!cards.length && <p className="bc-explore-empty">No passports match these filters.</p>}
      </div>
      <div className="bc-explore-navigation">
        <div className="bc-explore-zoom" role="group" aria-label="Collection canvas navigation">
          <button
            type="button"
            aria-label="Zoom out"
            disabled={camera.zoom <= 0.12}
            onClick={() => zoomAt(0.8, size.width / 2, size.height / 2, true)}
          >
            <MinusIcon aria-hidden />
          </button>
          <output aria-label="Canvas zoom">{Math.round(camera.zoom * 100)}%</output>
          <button
            type="button"
            aria-label="Zoom in"
            disabled={camera.zoom >= 1.5}
            onClick={() => zoomAt(1.25, size.width / 2, size.height / 2, true)}
          >
            <PlusIcon aria-hidden />
          </button>
          <button type="button" onClick={() => fit(true)} aria-label="Fit entire collection">
            <Squares2X2Icon aria-hidden /> Fit
          </button>
        </div>
        <button
          type="button"
          className="bc-explore-minimap"
          aria-label="Collection overview map; click to move the canvas"
          onClick={(event) => {
            if (!event.detail) {
              fit(true);
              return;
            }
            const rect = event.currentTarget.getBoundingClientRect();
            const current = cameraRef.current;
            moveCamera({
              ...current,
              x: size.width / 2 - ((event.clientX - rect.left) / rect.width) * world.width * current.zoom,
              y: size.height / 2 - ((event.clientY - rect.top) / rect.height) * world.height * current.zoom,
            });
          }}
        >
          <svg viewBox={`0 0 ${world.width} ${world.height}`} preserveAspectRatio="none" aria-hidden>
            {cards.map((card, index) => {
              const position = cardPosition(card, index);
              return (
                <rect
                  key={card.key}
                  x={(position.x / 100) * world.width - 75}
                  y={(position.y / 100) * world.height - 105}
                  width="150"
                  height="210"
                  fill="#fb923c"
                  fillOpacity=".3"
                  rx="12"
                />
              );
            })}
            <rect
              x={-camera.x / camera.zoom}
              y={-camera.y / camera.zoom}
              width={size.width / camera.zoom}
              height={size.height / camera.zoom}
              fill="white"
              fillOpacity=".05"
              stroke="#f2eee9"
              strokeWidth={3 / camera.zoom}
            />
          </svg>
        </button>
      </div>
      <div className="bc-explore-footer">
        <span>
          {editable ? 'Drag handles to arrange' : 'Drag to explore'}{' '}
          <span className="bc-explore-touch-hint">· Pinch to zoom</span>
          <span className="bc-explore-desktop-hint">· ⌘ / Ctrl + scroll to zoom</span>
        </span>
        <span>{editable ? 'Changes are a draft until you save.' : 'Select a card to inspect it.'}</span>
      </div>
    </section>
  );
}
